import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface SubscriptionDiscounts {
  id: string;
  weekly: number;
  monthly: number;
  yearly: number;
  sessionsWeekly: number;
  sessionsMonthly: number;
  sessionsYearly: number;
}

export function useSubscriptionDiscounts() {
  return useQuery({
    queryKey: ['subscription_discounts'],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<SubscriptionDiscounts> => {
      const { data, error } = await supabase
        .from('home_page_settings')
        .select('id, subscription_discount_weekly_pct, subscription_discount_monthly_pct, subscription_discount_yearly_pct, subscription_sessions_weekly, subscription_sessions_monthly, subscription_sessions_yearly')
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return {
        id: (data as any)?.id ?? '',
        weekly: Number((data as any)?.subscription_discount_weekly_pct ?? 5),
        monthly: Number((data as any)?.subscription_discount_monthly_pct ?? 12),
        yearly: Number((data as any)?.subscription_discount_yearly_pct ?? 20),
        sessionsWeekly: Number((data as any)?.subscription_sessions_weekly ?? 1),
        sessionsMonthly: Number((data as any)?.subscription_sessions_monthly ?? 4),
        sessionsYearly: Number((data as any)?.subscription_sessions_yearly ?? 48),
      };
    },
  });
}

export function useUpdateSubscriptionDiscounts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vals: { id: string; weekly: number; monthly: number; yearly: number; sessionsWeekly: number; sessionsMonthly: number; sessionsYearly: number; }) => {
      const { error } = await supabase
        .from('home_page_settings')
        .update({
          subscription_discount_weekly_pct: vals.weekly,
          subscription_discount_monthly_pct: vals.monthly,
          subscription_discount_yearly_pct: vals.yearly,
          subscription_sessions_weekly: vals.sessionsWeekly,
          subscription_sessions_monthly: vals.sessionsMonthly,
          subscription_sessions_yearly: vals.sessionsYearly,
        } as any)
        .eq('id', vals.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['subscription_discounts'] });
      qc.invalidateQueries({ queryKey: ['gathering_packages_pricing_v2'] });
    },
  });
}

export function discountForFrequency(d: SubscriptionDiscounts | undefined, freq: string): number {
  if (!d) return 0;
  if (freq === 'monthly') return d.monthly;
  if (freq === 'yearly') return d.yearly;
  return d.weekly;
}
